import { ObjectTools } from "@cervval.js/common/tools";
import { ACTION_TYPE, Action, ActionPersistData, ElementToCreate, ElementToDelete, Operation, OperationType } from "../action.model";
import { actualizeFieldValueIfNeeded } from "../services/cache.tools";

export class OpCreate<
    ElementTypes,
    TypeName extends keyof ElementTypes,
    ElementType extends ElementTypes[TypeName],
    AppLogInfos,
    Context
> implements Operation<ElementTypes, AppLogInfos, Context> {

    /** Action sends a copy of the element to create, because if it sends the reference it could also send incompatible updates (for example updating a foreign key that is not yet created).*/
    protected _clonedElementToCreate: ElementType;

    constructor(protected readonly _elementTypeName: TypeName, protected _elementToCreate: ElementType) {
        this._clonedElementToCreate = ObjectTools.clone(_elementToCreate);
    }

    /** @inheritdoc */
    public get elementTypeName(): keyof ElementTypes { return this._elementTypeName; }

    /** @inheritdoc */
    public getElement(): ElementType {
        return this._elementToCreate;
    }

    /**
     * (re)create element
     */
    public process(action: Action<ElementTypes, AppLogInfos, Context>): void {
        action.getCacheManager().getDTOCacheRepository(this._elementTypeName).push(this._elementToCreate);
    }

    /**
     * delete element
     */
    public undo(action: Action<ElementTypes, AppLogInfos, Context>): void {
        action.getCacheManager().getDTOCacheRepository(this._elementTypeName).remove(this._elementToCreate);
    }

    /**
     * Returns the type of the current Operation
     * @returns {OperationType}
     */
    public getOperationType(): OperationType {
        return OperationType.CREATE;
    }

    /**
     * Return action as string
     * @param action 
     * @returns {string}
     */
    public toString(action: Action<ElementTypes, AppLogInfos, Context>): string {
        return `Create ${String(this._elementTypeName)}: ${this._elementToCreate}`;
    }

    /**
     * update internal objects (retrieve the element from the cache or update ids and foreign keys)
     */
    public actualize(action: Action<ElementTypes, AppLogInfos, Context>): void {
        const cache = action.getCacheManager();
        this._elementToCreate = cache.getDTOCacheRepository(this._elementTypeName).getUpToDateElement(this._elementToCreate) as ElementType;
        // set the id of _clonedElementToCreate with the id of _elementToCreate (useful for redo)
        const dtoCacheRepository = cache.getDTOCacheRepository(this._elementTypeName);
        dtoCacheRepository.setId(this._clonedElementToCreate, dtoCacheRepository.getId(this._elementToCreate));
        // update ids on cloned object
        for (const k in this._clonedElementToCreate) {
            const foreignTable = cache.getForeignKeyTableName(this._elementTypeName, k as keyof ElementTypes[TypeName]);
            if (foreignTable != null) {
                // k is a foreign key, we get the up to date id
                this._clonedElementToCreate[k] = actualizeFieldValueIfNeeded(cache, foreignTable, this._clonedElementToCreate[k], {
                    // The DTO is also updated so next operations will have the correct id, because actualize is not called before process.
                    dto: this._elementToCreate,
                    field: k
                });
            }
        }
    }

    /**
     * Add server server action element to ActionPersistenceData
     */
    public addServerActionElement(actionPersistenceData: ActionPersistData<Context>, params: { action: Action<ElementTypes, AppLogInfos, Context>, actionType: ACTION_TYPE }): void {
        const dtoCacheRepository = params.action.getCacheManager().getDTOCacheRepository(this._elementTypeName);
        if (params.actionType === ACTION_TYPE.UNDO) {
            const elementsToDelete: ElementToDelete = {
                elementTypeName: String(this._elementTypeName),
                elementId: dtoCacheRepository.getId(this._elementToCreate)
            };
            if (!actionPersistenceData.elementsToDelete) {
                actionPersistenceData.elementsToDelete = [];
            }
            actionPersistenceData.elementsToDelete.push(elementsToDelete);
        } else {
            const elementsToCreate: ElementToCreate = {
                elementTypeName: String(this._elementTypeName),
                element: this._clonedElementToCreate,
                isRecreate: params.actionType === ACTION_TYPE.REDO
            };

            if (!actionPersistenceData.elementsToCreate) {
                actionPersistenceData.elementsToCreate = [];
            }
            actionPersistenceData.elementsToCreate.push(elementsToCreate);
        }
    }

    /** Put back _elementToCreate in the state as _clonedElementToCreate. 
     * This method is used by detached action during submission, when inserting it's modifications into global cache, and should probably not be used elsewhere.
     */
    public restoreElementToCreationState(): void {
        for (const k in this._elementToCreate) {
            this._elementToCreate[k] = this._clonedElementToCreate[k];
        }
    }

}